import { useRef, useState } from 'react';
import omit from 'lodash/fp/omit';
import isEmpty from 'lodash/fp/isEmpty';
import classNames from 'classnames';
import TableViewToggles from '@rio-cloud/rio-uikit/TableViewToggles';
import { TableViewTogglesViewType } from '@rio-cloud/rio-uikit';
import { useSelectVehicleStore } from '../../services/selectVehicleSlice';
import { ColumnDetailsMap } from './models/ColumnDetailsMap';
import { ColumnLabel } from './models/ColumnLabel';
import { IAnalysis } from './models/Analysis';
import { useAnalysisData } from './hooks/useAnalysisData';
import { useTableSettings } from './hooks/useTableSettings';
import { useWindowResize } from './hooks/useWindowResize';
import DashboardTableToolbar from './components/DashboardTableToolbar';
import DashboardTableBody from './components/DashboardTableBody';
import TableRowContent from './components/TableRowContent';
import { SidebarPortal } from '../../shared/components/SidebarPortal';
import { ExtractSidebar } from '../extractBehavior';
import { useLoaderData, useNavigate } from 'react-router';
import { extractLoader } from '../extractBehavior/extractLoader';
import style from './DashboardTableAnalysis.module.css'

export type DashboardTableProps = {
  viewType: TableViewTogglesViewType;
  behaviorType: string;
};

const columnLabelsSafe: ColumnLabel = {
  startTrip: 'Início da Trip',
  finalTrip: 'Final da Trip',
  tripScore: 'Score da Trip',
  tripCumulativeScore: 'Score Comulativo da Trip',
  epk: 'EPK',
  itSpeed: 'Velocidade',
  itAttentiveDriving: 'Condução',
  itBraking: 'Frenagem',
  itRollover: 'Condição de Estabilidade',
  itCoasting: 'Aproveitamento de Inércia',
  itGoodPractices: 'Cuidados com o veículo',
  extractLink: 'Extrato do Evento',
};

const columnLabelsPredictive: ColumnLabel = {
  startTrip: 'Início da Trip',
  finalTrip: 'Final da Trip',
  tripScore: 'Score da Trip',
  tripCumulativeScore: 'Score Comulativo da Trip',
  epk: 'EPK',
  itKickdown: 'Aceleração',
  itAcceleration: 'Partida',
  itRpm: 'Rotação do Motor',
  itClutch: 'Uso da Embreagem',
  itOperationalInsistence: 'Insistência Operacional',
  extractLink: 'Extrato do Evento',
};

const columnLabelsESG: ColumnLabel = {
  startTrip: 'Início da Trip',
  finalTrip: 'Final da Trip',
  tripScore: 'Score da Trip',
  tripCumulativeScore: 'Score Comulativo da Trip',
  epk: 'EPK',
  itIdling: 'Marcha Lenta',
  itIdlingAc: 'Climatização Evitada com Veículo Parado',
  extractLink: 'Extrato do Evento',
};

const disabledColumns: string[] = [];
const subRows: string[] = [];

const DashboardTableAnalysis = (props: DashboardTableProps) => {
  const { viewType: externalViewType, behaviorType } = props;
  const { selectedVehicle } = useSelectVehicleStore();
  const [searchValue, setSearchValue] = useState('');
  const [viewType, setViewType] = useState(externalViewType || TableViewToggles.VIEW_TYPE_TABLE);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  let columnLabels: ColumnLabel = columnLabelsSafe;
  let dashboardColumnOrder: string[] = [];

  switch(behaviorType){
    case 'SAFE':
      columnLabels = columnLabelsSafe;
      dashboardColumnOrder = Object.keys(columnLabels);
      break;
    case 'PREDICTIVE':
      columnLabels = columnLabelsPredictive;
      dashboardColumnOrder = Object.keys(columnLabels);
      break;
    case 'ESG':
      columnLabels = columnLabelsESG;
      dashboardColumnOrder = Object.keys(columnLabels);
      break;
  }
  

  //extract sidebar
  const navigate = useNavigate();
  const loaderData = useLoaderData<typeof extractLoader>();
  //extract sidebar

  const handleViewTypeChange = (newViewType: TableViewTogglesViewType) => setViewType(newViewType);

  const handleToggleRow = (id: string) => {
    setExpandedRows(prev => (prev.includes(id) ? prev.filter(expanded => expanded !== id) : [...prev, id]));
  };

  const {
    analysisList,
    isLoading,
    error,
  } = useAnalysisData({
    externalViewType,
    behaviorType
  });

  const {
    showTableSettingsDialog,
    hiddenColumns,
    columnsDetails,
    columnOrder,
    handleToggleTableSettingsDialog,
    handleColumnChange,
    handleColumnDetailsChange,
  } = useTableSettings({
    dashboardColumnOrder,
    columnsDetailsDescriptor: {} as ColumnDetailsMap,
    columnLabels,
    disabledColumns,
  });

  const menuRef = useRef(document.getElementsByClassName('AssetTree')[0]);
  const lcmCardRef = useRef(document.getElementsByClassName('StatsWidget-wrapper')[0]);
  const { width, screenWidth } = useWindowResize(menuRef, lcmCardRef);

  const columns = columnOrder.filter((name: string) => !hiddenColumns.includes(name) && !subRows.includes(name));
  const withoutHidden = omit(hiddenColumns);
  const filteredRows = analysisList.map((vehicle: IAnalysis) => ({ ...withoutHidden(vehicle) })) as IAnalysis[];

  const searchResult = !searchValue
    ? filteredRows
    : filteredRows.filter((row: Partial<IAnalysis>) =>
      columns.some((col: string) => {
        const value = row[col as keyof IAnalysis];
        if (value == null) return false;
        return row[col as keyof IAnalysis]?.toString().toLowerCase().includes(searchValue.toLowerCase());
      })
    );

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;
  const tableClassNames = classNames(
    'table',
    'table-layout-fixed',
    'table-column-overflow-hidden',
    'table-bordered',
    'table-sticky',
    'table-head-filled',
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD && 'table-cards table-single-card',
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS && 'table-cards table-multi-cards'
  );

  return (
    <div>
      <SidebarPortal>
        {loaderData && (
          <ExtractSidebar
            data={loaderData.response}
            onClose={() => {
              navigate({ search: "" });
            }}
            onBackdropClick={() => {
              navigate({ search: "" });
            }}
          />
        )}
      </SidebarPortal>
      <DashboardTableToolbar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        viewType={viewType}
        onViewTypeChange={handleViewTypeChange}
        selectedVehicle={selectedVehicle}
        onToggleSettings={handleToggleTableSettingsDialog}
        showTableSettingsDialog={showTableSettingsDialog}
        onHideSettings={handleToggleTableSettingsDialog}
        onColumnChange={handleColumnChange}
        onColumnDetailsChange={handleColumnDetailsChange}
        columnOrder={columnOrder}
        hiddenColumns={hiddenColumns}
        columnsDetails={columnsDetails}
        columnLabels={columnLabels}
        bestPracticesColumnOrder={dashboardColumnOrder}
        disabledColumns={disabledColumns}
        hasData={!isEmpty(analysisList)}
      />

      <div
        style={{
          overflowX: screenWidth < 1734 ? 'auto' : undefined,
          maxWidth: screenWidth < 1734 ? width : undefined,
          height: screenWidth < 1734 ? '40vh' : undefined,
        }}
      >
        <DashboardTableBody
          searchResult={searchResult}
          columns={columns}
          columnLabels={columnLabels}
          columnsDetails={columnsDetails}
          subRows={[]}
          tableClassNames={tableClassNames}
          isLoading={isLoading}
          isViewTypeTable={isViewTypeTable}
          screenWidth={screenWidth}
          width={width}
          expandedRows={expandedRows}
          handleToggleRow={handleToggleRow}
          TableRowContent={TableRowContent}
        />
      </div>
    </div>
  );
};

export default DashboardTableAnalysis;